import {
  SyntheticEvent, useState,
} from 'react';

// Third-party
import { useTranslation } from 'react-i18next';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { FormProvider, useForm } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';

// Components
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { TCustomTheme } from '@mgm/common/theme';
import { BaseTabs, MobileTabs } from '../../components/base';
import { TasklistTab } from './Tasklist';
import { SubmissionTab } from './Submission';
import { CustomRangePicker } from '../../components/pickers';

// Hooks
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import { useGetSubmissionsAPI } from './Shared/hooks/api';
import { useRangePicker } from '../../hooks/useCommon';

// Redux-toolkit
import { commonActions } from '../../store/common/slice';
import { tasklistsActions } from '../../store/tasklists/slice';

// CSS
import './tasklists.css';

// Types
import { TTasklist } from '../../types';
import { TCreateEditForm } from '../../components/form';
import { useTasklistPermissions } from './Shared/hooks/useTasklistPermissions';
import { getTokenDetails } from '../../store/authenticate/selectors';

const Tasklists = () => {
  const {
    isBtnDisabled, chosenDates, handleSetDates,
  } = useRangePicker();

  const { refetch: refetchSubmissions, isRefetching } = useGetSubmissionsAPI({ dates: chosenDates });
  const { t } = useTranslation('tasklists');
  const dispatch = useAppDispatch();
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const { tasklistPermissions } = useTasklistPermissions();
  const { settings } = useAppSelector(getTokenDetails);
  const [tab, setTab] = useState(!tasklistPermissions.isAllowViewTasklist ? '2' : '1');

  const methods = useForm<TCreateEditForm>({
    mode: 'all',
  });

  const tabs = [
    tasklistPermissions.isAllowViewTasklist && {
      label: t('tasklists'),
      value: '1',
    },
    {
      label: t('submissions'),
      value: '2',
    },
  ].filter((x) => !!x) as Array<{ label: string, value: string }>;

  const handleChange = (_event: SyntheticEvent, newValue: string) => {
    setTab(newValue);
    if (newValue === '1') {
      dispatch(tasklistsActions.setIsOpenAllSubmissionsNav(false));
    } else {
      dispatch(tasklistsActions.setIsOpenAllTasklistsNav(false));
    }
    dispatch(tasklistsActions.setSubmissionsTab('3'));
  };

  const openNav = () => {
    dispatch(tasklistsActions.setSelectedTasklistForAction({} as TTasklist));
    dispatch(commonActions.setIsOpenPagesNav(true));
  };

  const openTasklistsNav = () => {
    dispatch(tasklistsActions.setIsOpenAllTasklistsNav(true));
  };

  const openSubmissionsNav = () => {
    dispatch(tasklistsActions.setIsOpenAllSubmissionsNav(true));
  };

  const getSubmissions = async () => {
    await refetchSubmissions();
    dispatch(commonActions.toggleRangePicker(false));
  };

  const TabsComponent = isMobile ? MobileTabs : BaseTabs;

  return (
    <Box paddingTop={2}>
      <TabsComponent
        centered={false}
        tabs={tabs}
        value={tab}
        handleChange={handleChange}
        secondaryAction={(
          tab === '1'
            ? tasklistPermissions.isAllowManage && (
              <Stack {...(isMobile && {
                direction: 'row',
                spacing: 2,
              })}
              >
                <Button
                  fullWidth={isMobile}
                  onClick={openNav}
                  variant="contained"
                >
                  {t('addTasklist')}
                </Button>
                {isMobile && (
                <Button
                  fullWidth={isMobile}
                  onClick={openTasklistsNav}
                  variant="outlined"
                >
                  {t('allTasklists')}
                </Button>
                )}
              </Stack>
            )
            : (
              <Stack spacing={2}>
                <FormProvider {...methods}>
                  <Card sx={{
                    boxShadow: 'none',
                    width: isMobile ? '100%' : 'auto',
                  }}
                  >
                    <Stack alignItems="center" spacing={2} direction="row">
                      <CustomRangePicker
                        format={settings.ui?.dateFormat}
                        defaultValue={chosenDates}
                        setDates={handleSetDates}
                      />
                      <LoadingButton
                        disabled={isBtnDisabled}
                        variant="contained"
                        size="small"
                        onClick={getSubmissions}
                        loading={isRefetching}
                      >
                        {t('loadSubmissions')}
                      </LoadingButton>
                    </Stack>
                  </Card>
                </FormProvider>
                {isMobile && (
                <Button
                  fullWidth={isMobile}
                  onClick={openSubmissionsNav}
                  variant="outlined"
                >
                  {t('allSubmissions')}
                </Button>
                )}
              </Stack>
            )
        )}
      >
        <TabPanel
          sx={{
            paddingX: 0,
          }}
          value="1"
        >
          <TasklistTab />
        </TabPanel>
        <TabPanel
          sx={{
            paddingX: 0,
          }}
          value="2"
        >
          <SubmissionTab />
        </TabPanel>
      </TabsComponent>
    </Box>
  );
};

export {
  Tasklists,
};
